import CustomElement from '~utilities/custom-element';
import DetectTouch from '~utilities/detect-touch';
import TabIndexChildren from '~utilities/tab-index-children';
import DetectBreakpoints from '~utilities/detect-breakpoints';
import breakpoints from 'scss-settings/_settings.grid.scss';

const IS_DISABLED = 'is--disabled';
const IS_ACTIVE = 'is--active';

class Carousel extends CustomElement {
    constructor() {
        super();

        this.checkBreakpoints();

        this.skip = parseInt(this.getAttribute('js-skip'));
        const transitionDuration = parseInt(this.getAttribute('js-transition'));
        this.transitionDuration = transitionDuration ? transitionDuration : 300;
    }

    removeAttributes() {
        [...this.attributes].forEach(attribute => {
            if(!attribute.name.includes("viewable")) this.removeAttribute(attribute.name);
        });
    }

    static get observedAttributes() { 
        return ['js-next', 'js-prev', 'js-go-to']; 
    }

    resetCarousel() {
        window.addEventListener('resize', (event) => {
            this.viewable = false;
            this.checkBreakpoints();
            this.setWidthVariables();
            if(this.center) this.setCenter();
        }, {passive: true});
    }

    checkBreakpoints() {
        const envs = Object.keys(breakpoints);
        const breakpoint = envs.indexOf(DetectBreakpoints.breakpoint);

        envs.forEach(env => {
            const minBreakpoint = envs.indexOf(env);

            if(this.hasAttribute(`js-viewable-${env}`) && breakpoint >= minBreakpoint) {
                this.viewable = parseInt(this.getAttribute(`js-viewable-${env}`));
            }
        })

        if(!this.viewable) this.viewable = parseInt(this.getAttribute('js-viewable'));
    }

    setWidthVariables() {
        const containerStyle = getComputedStyle(this.container);
        this.containerSize = this.vertical ? this.container.offsetHeight - (parseFloat(containerStyle.paddingLeft) + parseFloat(containerStyle.paddingRight)) : this.container.offsetWidth - (parseFloat(containerStyle.paddingLeft) + parseFloat(containerStyle.paddingRight));
        this.innerSize = this.containerSize * (this.items.length / this.viewable);
        this.itemSize = this.containerSize / this.viewable;
        
        const widthOrHeight = this.vertical ? `height` : `width`;
        this.inner.style[widthOrHeight] = `${this.innerSize}px`;
    }

    callback() {
        super.callback();

        this.container = this.element.querySelector('[js-carousel-container]');
        this.inner = this.element.querySelector('[js-carousel-inner]');
        this.items = [...this.element.querySelectorAll('[js-carousel-item]')];
        this.next = this.element.querySelector('[js-carousel-next]');
        this.prev = this.element.querySelector('[js-carousel-prev]');

        this.position = {};
        this.total = {};
        this.position.item = 1;
        this.position.slide = 'not yet set';
        this.position.inner = 0;
        this.total.items = this.items.length;
        this.total.slides = Math.ceil(this.items.length / this.viewable);

        if(this.total.items) {
            this.options(true);
            this.setWidthVariables();
            
            if(this.center) this.setCenter();
            if(this.loop) this.setLoop();
            if(this.indicator) this.setIndicator();
            
            this.showActiveItem();
            this.bindEvents();
        }

        if(this.total.items <= this.viewable && !this.center || !this.total.items || this.center && this.viewable / 2 >= this.total.items) {
            this.prev.classList.add(IS_DISABLED);
            this.next.classList.add(IS_DISABLED);
        }

        this.resetCarousel();
    }

    options(noLoop = false) {
        let attributes = this.getAttribute('js-options');
        if(!attributes) return;
        attributes = attributes.split('|');
        
        attributes.forEach(attribute => {
            switch(attribute) {
                case 'vertical':
                    this.vertical = true;
                    break;
                case 'loop':
                    if(noLoop) this.loop = true;
                    break;
                case 'center':
                    this.center = true;
                    break;
                case 'indicator':
                    this.indicator = true;
                    break;
                default:
                    console.warn(`carousel.options(): "${attribute}" is not an option.`);
                    break;
            }
        });
    }

    dragStart() {
        const moveEvent = DetectTouch.hasTouch ? 'touchmove' : 'mousemove';
        const upEvent = DetectTouch.hasTouch ? 'touchend' : 'mouseup';

        let dragActionListener = (event) => this.dragAction(event);
        let endActionListener = () => { 
            this.inner.removeEventListener(moveEvent, dragActionListener); 
            this.inner.removeEventListener("mouseleave", endActionListener); 
            this.inner.removeEventListener(upEvent, endActionListener); 
            this.dragEnd();
        };
        this.dragMovement = 0;
        
        this.inner.addEventListener(upEvent, endActionListener, {passive: true});
        this.inner.addEventListener('mouseleave', endActionListener, {passive: true});
        this.inner.addEventListener(moveEvent, dragActionListener, {passive: true});
    }

    dragEnd() {
        const currentPosition = this.position.inner;
        const dragPosition = currentPosition - this.dragMovement;
        const halfNextPosition = currentPosition + ((this.skip * this.itemSize) / 2);
        const halfPrevPosition = currentPosition - ((this.skip * this.itemSize) / 2);
        this.inner.classList.remove('is--dragging');

        if(DetectTouch.hasTouch) this.prevTouchPage = undefined;

        if(halfNextPosition < dragPosition) {
            this.goToNext();
        } else if(halfPrevPosition > dragPosition) {
            this.goToPrev();
        } else {
            this.goTo(this.position.item);
        }
    }

    dragAction(event) {
        this.inner.classList.add('is--dragging');
        
        if(DetectTouch.hasTouch) {
            const newTouchPage = this.vertical ? event.touches[0].clientY : event.touches[0].clientX;
            if(!this.prevTouchPage) this.prevTouchPage = newTouchPage;
            this.dragMovement += newTouchPage - this.prevTouchPage;
            this.prevTouchPage = newTouchPage
        } else {
            this.dragMovement += this.vertical ? event.movementY : event.movementX;
        };

        const dragPosition = this.position.inner - this.dragMovement;
        const dragTranslatePosition = dragPosition > 0 ? -Math.abs(dragPosition) : Math.abs(dragPosition);
        this.inner.style.transform = this.vertical ? `translate3d(0,${dragTranslatePosition}px,0)` : `translate3d(${dragTranslatePosition}px,0,0)`;
    }

    setTransition(transition) {
        return this.inner.style.transitionDuration = transition ? `${this.transitionDuration}ms` : `0ms`;
    }

    bindEvents() {
        const downEvent = DetectTouch.hasTouch ? 'touchstart' : 'mousedown';
        this.inner.addEventListener(downEvent, () => this.dragStart(), {passive: true});
        if(this.next) this.next.addEventListener("click", () => this.goToNext(), {passive: true});
        if(this.prev) this.prev.addEventListener("click", () => this.goToPrev(), {passive: true});

        this.element.addEventListener('keyup', event => {
            if (event.code === 'ArrowRight') this.goToNext();
            if (event.code === 'ArrowLeft') this.goToPrev();
        }, {passive: true});
    }

    goToNext() {
        this.goTo(this.position.item + this.skip);
    }
    
    goToPrev() {
        this.goTo(this.position.item - this.skip);
    }

    setCenter() {
        this.center = true;
        const center = (this.containerSize / 2) - (this.itemSize / 2);
        this.inner.style.marginLeft = `${center}px`;
    }
    setIndicator () {
        let indicatorTemplate = `<div class="carousel__indicator" js-carousel-indicator>`;
        for(let i = 1; i <= this.total.items; i++) {
            indicatorTemplate += `<button aria-label='Slide ${i} of ${this.total.items}' class="carousel__indicator-item${this.position.item == i ? ' is--active' : ''}" data-albel js-carousel-indicator-item='${i}'></button>`;
        }
        indicatorTemplate += `</div>`;

        this.element.insertAdjacentHTML('beforeend', indicatorTemplate);

        const indocatorButtons = [...this.element.querySelectorAll('[js-carousel-indicator-item]')];

        indocatorButtons.forEach(button => {
            button.addEventListener('click', () => {
                this.setAttribute('js-go-to', button.getAttribute('js-carousel-indicator-item'));
            }, {passive: true})
        });
    }

    setLoop() {
        this.hasLoop = true;

        const extraClonedSize = (this.itemSize * this.viewable) * 2;
        this.clonedBefore = document.createElement('div');
        this.clonedAfter = document.createElement('div');
        
        this.clonedBefore.classList.add('carousel__cloned');
        this.clonedBefore.classList.add('carousel__cloned--start');
        this.clonedAfter.classList.add('carousel__cloned');
        this.clonedAfter.classList.add('carousel__cloned--end');

        for (let i = 1; i <= this.viewable * 2; i++) {
            this.clonedAfter.insertAdjacentElement('beforeend', this.items[i - 1].cloneNode(true));
            this.clonedBefore.insertAdjacentElement('afterbegin', this.items[this.items.length - i].cloneNode(true));
        }
        
        this.inner.insertAdjacentElement('afterbegin', this.clonedBefore);
        this.inner.insertAdjacentElement('beforeend', this.clonedAfter);
        const widthOrHeight = this.vertical ? 'height' : 'width';
        this.clonedBefore.style[widthOrHeight] = `${extraClonedSize}px`;
        this.clonedAfter.style[widthOrHeight] = `${extraClonedSize}px`;
    }

    isNotLooping(newPosition) {
        const viewableMinusPosition = this.viewable - 1;
        let lastItemOfNewPositon = this.center ? newPosition : newPosition + viewableMinusPosition;

        if(this.total.items > this.viewable && !this.center || this.center && this.total.items > this.viewable / 2) {
            this.prev.classList.remove(IS_DISABLED);
            this.next.classList.remove(IS_DISABLED);
        }
        
        // If first slide
        if(newPosition <= 1) {
            newPosition = 1;
            this.prev.classList.add(IS_DISABLED);
        }
        // If last slide
        if(lastItemOfNewPositon >= this.items.length) {
            newPosition = this.center ? this.items.length : this.items.length - viewableMinusPosition;
            this.next.classList.add(IS_DISABLED);
        }

        return newPosition;
    }

    isLooping(newPosition) {
        let disableTransition= false;
        const leftOutside = newPosition < 0;
        const rightOutside = newPosition > this.total.items;

        if(leftOutside || rightOutside) disableTransition = true;

        if(disableTransition) {
            const setToNewPosition = setTimeout(() => {
                this.setTransition(false);
                if(leftOutside) this.goTo(newPosition + this.total.items, false);
                if(rightOutside) this.goTo(newPosition - this.total.items, false);
                clearTimeout(setToNewPosition);
            }, this.transitionDuration);
            setToNewPosition;
        }

        return newPosition;
    }

    activeIndicator(newPosition) {
        let newItem = (newPosition - 1) % this.total.items;
        if (newItem < 0) newItem = newItem + this.total.items;
        const indocatorButtons = [...this.element.querySelectorAll('[js-carousel-indicator-item]')];
        indocatorButtons.forEach((button) => {
            button.classList.remove(IS_ACTIVE);
        });
        
        indocatorButtons[newItem].classList.add(IS_ACTIVE);
    }

    goTo(newPosition, transition = true) {
        if(this.indicator) this.activeIndicator(newPosition);
        // Check loop option
        newPosition = this.hasLoop ? this.isLooping(newPosition) : this.isNotLooping(newPosition);

        // Set position
        const innerPosition = this.itemSize * (newPosition - 1);
        const innerTranslatePosition = innerPosition > 0 ? -Math.abs(innerPosition) : Math.abs(innerPosition);
        if(this.vertical) {
            this.inner.style.transform = `translate3d(0,${innerTranslatePosition}px,0)`;
        } else {
            this.inner.style.transform = `translate3d(${innerTranslatePosition}px,0,0)`;
        }

        this.position.item = newPosition;
        this.position.inner = innerPosition;

        // Set transition back
        if(transition) this.setTransition(true);

        this.showActiveItem();
    }

    addActiveItem(item, index, notCloned = false) {
        let activeIndex = this.position.item - 1;
        const totalIndex = this.total.items - 1;
        
        if(activeIndex > totalIndex) activeIndex = activeIndex - this.total.items;
        if(activeIndex < 0) activeIndex = activeIndex + this.total.items;

        const toggleActive = index === activeIndex ? 'add' : 'remove' ;
        const toggleInView = this.ifItemInView(activeIndex, index) ? 'add' : 'remove' ;
        item.classList[toggleActive]('is--active');
        new TabIndexChildren(item, notCloned ? index === activeIndex ? 0 : -1 : -1, true);
        item.classList[toggleInView]('is--in-view');
    }

    showActiveItem() {
        this.items.forEach((item, index) => {
            this.addActiveItem(item, index, true);
        });

        if(this.hasLoop) {
            const clonedBeforeItems = Array.from(this.clonedBefore.children);
            clonedBeforeItems.forEach((item, index) => {
                const currentIndex = (this.total.items - (this.viewable * 2)) + index;
                this.addActiveItem(item, currentIndex);
            });
            
            const clonedAfterItems = Array.from(this.clonedAfter.children);
            clonedAfterItems.forEach((item, index) => {
                this.addActiveItem(item, index);
            });
        }
    }

    ifItemInView(activeIndex, index) {
        const viewableIndex = this.viewable - 1;
        const firstInViewIndex = this.center ? activeIndex - (viewableIndex / 2) : activeIndex ;
        const firstInViewIndexRounded = Math.ceil(firstInViewIndex);
        const lastInViewIndex = firstInViewIndex + viewableIndex;
        const lastInViewIndexRounded = Math.floor(lastInViewIndex);
        const totalIndex = this.total.items - 1;
        const inViewIndexes = [];
        
        for(let i = firstInViewIndexRounded; i <= lastInViewIndexRounded; i++) {
            let newInViewIndex = i;
            if(newInViewIndex > totalIndex) newInViewIndex = newInViewIndex - this.total.items;
            if(newInViewIndex < 0) newInViewIndex = newInViewIndex + this.total.items;
            inViewIndexes.push(newInViewIndex);
        }

        return inViewIndexes.includes(index);
    }

    attributeChangedCallback(name, oldValue, newValue) {
        switch(name) {
            case 'js-next':
                this.goToNext();
                break;
            case 'js-prev':
                this.goToPrev();
                break;
            case 'js-go-to':
                this.goTo(parseInt(newValue));
                break;
            default:
                console.warn(this, `${name}="${newValue}" isn't triggering anything. Make sure you've added a attributeChangedCallback function.`);
          }
    }
}

export default Carousel;
